import React from "react";

/**
 * Renders a small, safe subset of Markdown:
 *   - Links:   [label](https://example.com)
 *   - Bare URLs:  https://example.com  (auto-linked)
 *   - Bold:    **text**
 *   - Italic:  *text* or _text_
 *   - Line breaks are preserved.
 *
 * No raw HTML is ever rendered, so admin-authored content can't inject markup.
 */

const URL_RE = /https?:\/\/[^\s)]+/;

// Order matters: links first so URLs inside [..](..) aren't double-matched.
const RULES = [
    { type: "link", re: /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/ },
    { type: "autolink", re: URL_RE },
    { type: "bold", re: /\*\*([^*]+)\*\*/ },
    { type: "italic", re: /\*([^*]+)\*|_([^_]+)_/ },
];

const linkClass =
    "underline text-gray-100 hover:text-white break-words";

function parseInline(text, keyPrefix) {
    // Find the earliest matching rule in the remaining text.
    let earliest = null;
    for (const rule of RULES) {
        const match = rule.re.exec(text);
        if (match && (earliest === null || match.index < earliest.match.index)) {
            earliest = { rule, match };
        }
    }

    if (!earliest) {
        return text ? [text] : [];
    }

    const { rule, match } = earliest;
    const before = text.slice(0, match.index);
    const after = text.slice(match.index + match[0].length);
    const nodes = [];

    if (before) nodes.push(before);

    const key = `${keyPrefix}-${match.index}`;

    if (rule.type === "link") {
        nodes.push(
            <a
                key={key}
                href={match[2]}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
            >
                {match[1]}
            </a>
        );
    } else if (rule.type === "autolink") {
        nodes.push(
            <a
                key={key}
                href={match[0]}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
            >
                {match[0]}
            </a>
        );
    } else if (rule.type === "bold") {
        nodes.push(
            <strong key={key}>{parseInline(match[1], key)}</strong>
        );
    } else if (rule.type === "italic") {
        const content = match[1] || match[2];
        nodes.push(<em key={key}>{parseInline(content, key)}</em>);
    }

    return nodes.concat(parseInline(after, `${keyPrefix}-rest`));
}

export default function RichText({ text, className }) {
    if (!text) return null;

    return (
        <span className={className}>
            {parseInline(String(text), "rt")}
        </span>
    );
}